import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Bell,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Clock3,
  FileText,
  Folder,
  Image,
  Layers3,
  Mail,
  Monitor,
  Search,
  Shield,
  Sparkles,
  Star,
  Users,
  Zap
} from "lucide-react";
import {
  captureLead,
  completeCheckoutSession,
  createCheckoutSession,
  fetchPrices,
  trackPageViewServerEvent,
  trackLeadServerEvent,
  trackViewContentServerEvent,
  type StripePlanPrice,
  type StripePricesResponse
} from "./api";
import {
  captureCheckoutCompletePageview,
  captureFunnelScreenPageview,
  CHECKOUT_FBP_WAIT_MS,
  firePageViewBrowser,
  getPendingCheckoutTracking,
  getTrackingContext,
  initAnalytics,
  registerFunnelPersonProperties,
  rememberPendingCheckoutTracking,
  track,
  trackMetaInitiateCheckout,
  trackMetaLead,
  trackMetaPurchase,
  trackMetaViewContent,
  upgradeExternalIdToLead,
  waitForFbp
} from "./analytics";
import {
  defaultCheckoutOffer,
  getCheckoutOfferFromLocation,
  isPolarTrialOffer,
  type CheckoutOffer
} from "./checkoutOffer";
import { getOrCreateLandingAttribution } from "./attribution";
import { brand, config } from "./config";
import {
  feedbackByQuestionId,
  paymentExplainerSteps,
  paymentFaqs,
  checkoutPlans,
  plans,
  questions,
  testimonials,
  valueChips,
  type PlanId,
  type Question
} from "./funnelData";
import {
  DEFAULT_FUNNEL_CONCEPT,
  getFunnelConceptFromLocation,
  type FunnelConcept
} from "./funnelConcept";
import { getFunnelScreenName, getHashForScreen, getPaymentStep, parseHashToScreen } from "./funnelRoutes";
import { hasPrivacyOptOutSignal } from "./privacyControls";

type Answers = Record<string, string>;
type FeedbackContent = {
  title: string;
  highlightedTitle?: string;
  description: string;
  bullets: string[];
};

type StoredFunnelState = {
  step?: number;
  answers?: Answers;
  email?: string;
  selectedPlan?: PlanId;
  leadId?: number | null;
  pendingFeedbackQuestionId?: string | null;
  entryConceptKey?: string;
};

const STORAGE_KEY = "linkhiv:funnel:v2";
const CHECKOUT_TOKEN_KEY = "linkhiv:checkout-completion-token";
/** One ViewContent per tab session when leaving welcome (CTA or tap anywhere on hero). */
const META_VIEW_CONTENT_SESSION_KEY = "linkhiv:meta-view-content-fired";
/** One Lead event per email per tab session (prevents back/forward duplicate firing). */
const META_LEAD_SESSION_PREFIX = "linkhiv:meta-lead-fired:";
/** One CAPI PageView per tab session to mirror browser Pixel PageView. */
const META_PAGE_VIEW_SESSION_KEY = "linkhiv:meta-page-view-fired";
const EMAIL_STEP_QUERY_PARAM = "collect_email";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TRUST_AVATARS = [
  "/assets/reviews/girl-1.webp",
  "/assets/reviews/man-1.webp",
  "/assets/reviews/girl-2.webp",
  "/assets/reviews/darshan-patel-headshot-96w.webp"
] as const;

const getInitialEntryConcept = (): FunnelConcept => {
  if (typeof window === "undefined") return DEFAULT_FUNNEL_CONCEPT;

  const conceptFromUrl = getFunnelConceptFromLocation(window.location);
  if (conceptFromUrl) return conceptFromUrl;
  return DEFAULT_FUNNEL_CONCEPT;
};

const CURRENCY_PREFIX: Record<string, string> = {
  aud: "AU$",
  usd: "US$",
  cad: "CA$",
  nzd: "NZ$",
  gbp: "£"
};

const money = (unitAmount: number, currency: string): string => {
  const normalizedCurrency = currency.trim().toLowerCase();
  const amount = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: unitAmount % 100 === 0 ? 0 : 2
  }).format(unitAmount / 100);
  const prefix = CURRENCY_PREFIX[normalizedCurrency];
  if (prefix) return `${prefix}${amount}`;

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: unitAmount % 100 === 0 ? 0 : 2
  }).format(unitAmount / 100);
};

const annualMoney = (price: StripePlanPrice | undefined, fallback: string): string =>
  price ? `${money(price.unitAmount, price.currency)}/year` : fallback;

const normalizeEmail = (value: string): string => value.trim().toLowerCase();

const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(value);

const parseBooleanQueryFlag = (location: Location, queryParam: string): boolean => {
  const rawValue = location.search ? new URLSearchParams(location.search).get(queryParam)?.trim().toLowerCase() : null;
  return rawValue === "1" || rawValue === "true" || rawValue === "yes" || rawValue === "on";
};

const shouldShowEmailStepFromLocation = (location: Location): boolean =>
  parseBooleanQueryFlag(location, EMAIL_STEP_QUERY_PARAM);

const formatCheckoutCtaPrice = (price: string): string =>
  price.startsWith("$") ? `A${price}` : price;

const getRecommendedPlan = (answers: Answers): PlanId =>
  answers.usage === "one_mac" ? "standard" : "pro";

const findCheckoutPlan = (planId: PlanId) =>
  checkoutPlans.find((plan) => plan.id === planId) ?? checkoutPlans[0];

type DisplayCheckoutPlan = {
  id: PlanId;
  name: string;
  originalPrice?: string;
  price: string;
  detail: string;
  description: string;
  cta: string;
  badge?: string;
  badgeVariant?: "primary" | "outline";
  features: string[];
  priceTerm: string;
};

const getDisplayCheckoutPlans = (
  checkoutOffer: CheckoutOffer,
  prices?: StripePricesResponse | null
): DisplayCheckoutPlan[] => {
  if (!isPolarTrialOffer(checkoutOffer)) {
    return checkoutPlans.map((plan) => ({
      ...plan,
      features: [...plan.features],
      priceTerm: "one-time"
    }));
  }

  return [
    {
      id: "standard",
      name: "Standard Trial",
      price: checkoutOffer.priceDisplay,
      detail: `${checkoutOffer.trialDays}-day trial`,
      description: "Try the full Standard workflow on one Mac before the subscription renews.",
      cta: "Start trial",
      priceTerm: `for ${checkoutOffer.trialDays} days`,
      features: [
        "Free during trial",
        `Then ${annualMoney(prices?.standard, checkoutOffer.planConfig.standard?.renewalDisplay ?? "AU$29/year")}`,
        "Use on 1 Mac",
        "Full feature set",
        "Cancel before renewal"
      ]
    },
    {
      id: "pro",
      name: "Pro Trial",
      price: checkoutOffer.priceDisplay,
      detail: `${checkoutOffer.trialDays}-day trial`,
      description: "Try the full Pro workflow before the subscription renews.",
      cta: "Start trial",
      badge: "Trial offer",
      badgeVariant: "primary",
      priceTerm: `for ${checkoutOffer.trialDays} days`,
      features: [
        "Free during trial",
        `Then ${annualMoney(prices?.pro, checkoutOffer.planConfig.pro?.renewalDisplay ?? "AU$39/year")}`,
        "Use on up to 5 Macs",
        "Full feature set",
        "Cancel before renewal"
      ]
    }
  ];
};

const findDisplayCheckoutPlan = (
  displayPlans: DisplayCheckoutPlan[],
  planId: PlanId
): DisplayCheckoutPlan => displayPlans.find((plan) => plan.id === planId) ?? displayPlans[0];

const getTrialRenewalDisplay = (
  checkoutOffer: CheckoutOffer,
  planId: PlanId,
  prices?: StripePricesResponse | null
): string =>
  annualMoney(
    prices?.[planId],
    checkoutOffer.planConfig[planId]?.renewalDisplay ?? (planId === "standard" ? "AU$29/year" : "AU$39/year")
  );

const getTrialRenewalCopy = (
  checkoutOffer: CheckoutOffer,
  planId: PlanId,
  prices?: StripePricesResponse | null
): string => `Then ${getTrialRenewalDisplay(checkoutOffer, planId, prices)} unless canceled.`;

const getTrialIntroCopy = (checkoutOffer: CheckoutOffer): string =>
  checkoutOffer.priceAmount <= 0
    ? `Start free for ${checkoutOffer.trialDays} days.`
    : `Start with ${checkoutOffer.priceDisplay} for ${checkoutOffer.trialDays} days.`;

function LegalAgreementNote({ className = "" }: { className?: string }) {
  return (
    <p className={`legal-agreement-note${className ? ` ${className}` : ""}`}>
      By continuing, you agree to Linkhiv&apos;s{" "}
      <a href={brand.termsUrl} target="_blank" rel="noreferrer">
        Terms
      </a>{" "}
      and acknowledge our{" "}
      <a href={brand.privacyPolicyUrl} target="_blank" rel="noreferrer">
        Privacy Policy
      </a>.
    </p>
  );
}

const summarizeAnswers = (answers: Answers): string[] => {
  const labels = questions.flatMap((question) =>
    question.options
      .filter((option) => answers[question.id] === option.id)
      .map((option) => option.label)
  );

  return labels.length ? labels : ["Private Mac workflow", "Fast recall", "Calm clipboard history"];
};

const getSelectedOption = (questionId: string, answers: Answers): Question["options"][number] | undefined => {
  const question = questions.find((item) => item.id === questionId);
  return question?.options.find((option) => option.id === answers[questionId]);
};

type ResultInsights = {
  lostItem: string;
  friction: string;
  recallPattern: string;
  privacySignal: string;
  quickTipTitle: string;
  quickTip: string;
  weeklyRange: string;
  reclaimedHours: number;
};

const getResultInsights = (answers: Answers): ResultInsights => {
  const copyType = getSelectedOption("copy_types", answers);
  const workflowDrag = answers.workflow_drag;
  const recallSpeed = answers.recall_speed;
  const privacy = answers.privacy;

  const lostItemByChoice: Record<string, string> = {
    screenshots: "screenshots",
    images_and_text: "copied text and images",
    files: "files and folders",
    email: "email details"
  };

  const frictionByChoice: Record<string, string> = {
    reopening: "Re-finding old copies",
    handoff: "Moving between apps",
    cleanup: "Clipboard clutter"
  };

  const recallByChoice: Record<string, { label: string; range: string; hours: number }> = {
    daily: {
      label: "many times a day",
      range: "6-8 hrs/week",
      hours: 7
    },
    few_times: {
      label: "a few times a week",
      range: "3-5 hrs/week",
      hours: 4
    },
    only_when_lost: {
      label: "only when something goes missing",
      range: "1-3 hrs/week",
      hours: 2
    }
  };

  const privacyByChoice: Record<string, string> = {
    must_be_local: "local-only is non-negotiable",
    prefer_local: "local-first strongly matters",
    mostly_speed: "speed is your main buying trigger"
  };

  const quickTipByCopyType: Record<string, { title: string; tip: string }> = {
    screenshots: {
      title: "Create a screenshot inbox on your Mac",
      tip: "Press Shift-Command-5, choose Options, then set Save to a dedicated Screenshots Inbox folder. For the rest of the day, every capture lands in one place instead of being scattered across Desktop, Downloads, and messages."
    },
    images_and_text: {
      title: "Make three Mac text shortcuts",
      tip: "Open System Settings > Keyboard > Text Replacements and add three snippets you re-copy constantly, such as ;intro, ;link, or ;followup. The semicolon keeps them from firing inside normal words."
    },
    files: {
      title: "Pin a working-file tag in Finder",
      tip: "Create one Finder tag named Today, pin it in the Finder sidebar, and tag active files as you touch them. It gives you a temporary retrieval lane for the files you are most likely to need again."
    },
    email: {
      title: "Turn repeat details into Mac text replacements",
      tip: "Open System Settings > Keyboard > Text Replacements and save your most repeated contact lines behind triggers like ;phone, ;address, or ;booking. It is faster than searching old emails for the same details."
    }
  };

  const quickTipByFriction: Record<string, { title: string; tip: string }> = {
    reopening: {
      title: "Use Mac Spotlight as a two-minute recall check",
      tip: "Before reopening three apps, press Command-Space and search one exact word from the thing you copied. If Spotlight cannot surface it in two minutes, treat that as a signal you need a clipboard recall layer."
    },
    handoff: {
      title: "Give Mac handoffs one visible staging lane",
      tip: "Create a Today tag in Finder and pin it to the sidebar. When moving between apps, tag the file, image, or folder before switching so the handoff has one visible place to land."
    },
    cleanup: {
      title: "Use Mac-searchable prefixes for reusable snippets",
      tip: "For text you reuse, add a short prefix like LH: or Client: before saving it in Notes or Text Replacements. Prefixes make Spotlight and app search far more precise when history gets noisy."
    }
  };

  const recall = recallByChoice[recallSpeed] ?? recallByChoice.few_times;
  const quickTip = quickTipByCopyType[answers.copy_types] ?? quickTipByFriction[workflowDrag] ?? quickTipByFriction.reopening;

  return {
    lostItem: lostItemByChoice[answers.copy_types] ?? copyType?.label.toLowerCase() ?? "clipboard items",
    friction: frictionByChoice[workflowDrag] ?? "Copy-and-paste friction",
    recallPattern: recall.label,
    privacySignal: privacyByChoice[privacy] ?? "private Mac workflow matters",
    quickTipTitle: quickTip.title,
    quickTip: quickTip.tip,
    weeklyRange: recall.range,
    reclaimedHours: recall.hours
  };
};

const getWorkflowFeedbackContent = (answers: Answers): FeedbackContent => {
  const copyType = answers.copy_types;
  const workflowDrag = answers.workflow_drag;

  const contentByCopyType: Record<string, FeedbackContent> = {
    screenshots: {
      title: "Lost",
      highlightedTitle: "screenshots",
      description: "Keep them organized and easy to pull back up.",
      bullets: ["Find screenshots without digging", "Keep visual history in one place", "No cloud workspace"]
    },
    images_and_text: {
      title: "Lost",
      highlightedTitle: "copied text/images",
      description: "Keep them organized and ready when you need them.",
      bullets: ["Keep text and visuals within reach", "Recall older copies fast", "No cloud workspace"]
    },
    files: {
      title: "Lost",
      highlightedTitle: "files and folders",
      description: "Keep them organized and easy to paste back in.",
      bullets: ["Keep files and folders within reach", "Paste back without hunting through Finder", "No cloud workspace"]
    },
    email: {
      title: "Lost",
      highlightedTitle: "email details",
      description: "Keep them organized and easy to bring back.",
      bullets: ["Keep important details close", "Find copied email info fast", "No cloud workspace"]
    }
  };

  const workflowContextByChoice: Record<string, string> = {
    reopening: "No reopening apps just to find it again.",
    handoff: "Keep it connected as you move between apps.",
    cleanup: "Useful history, without the clutter."
  };

  const base = contentByCopyType[copyType] ?? {
    title: "That is exactly what Linkhiv helps with.",
    description: "One timeline keeps every clip in reach.",
    bullets: ["Hover preview before paste", "Files and screenshots grouped", "No cloud workspace"]
  };

  return {
    ...base,
    description: workflowContextByChoice[workflowDrag]
      ? `${base.description} ${workflowContextByChoice[workflowDrag]}`
      : base.description
  };
};

const getPendingFeedbackContent = (
  pendingFeedbackQuestionId: string | null,
  answers: Answers
): FeedbackContent | null => {
  if (!pendingFeedbackQuestionId) return null;
  if (pendingFeedbackQuestionId === "workflow_drag") {
    return getWorkflowFeedbackContent(answers);
  }
  return feedbackByQuestionId[pendingFeedbackQuestionId] ?? null;
};

function CtaTrustLines({ align = "center" }: { align?: "center" | "start" }) {
  return (
    <div
      className={align === "start" ? "cta-trust-lines cta-trust-lines--start" : "cta-trust-lines"}
      role="note"
    >
      <span className="cta-trust-lines__item">
        <Icon name="check" />
        <span className="cta-trust-lines__text">One-time purchase</span>
      </span>
    </div>
  );
}

function WelcomeLaurel({ side }: { side: "left" | "right" }) {
  return (
    <svg
      className={`welcome-laurel welcome-laurel--${side}`}
      width="30"
      height="44"
      viewBox="0 0 36 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M22 48Q8 26 18 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M18 40.7Q15.8 36.9 11.5 36Q13.7 39.8 18 40.7Z" fill="currentColor" />
      <path d="M18 40.7Q20.2 36.9 18.8 32.7Q16.6 36.5 18 40.7Z" fill="currentColor" />
      <path d="M15.3 33.3Q13.8 29.2 9.7 27.7Q11.2 31.8 15.3 33.3Z" fill="currentColor" />
      <path d="M15.3 33.3Q18.1 29.9 17.4 25.6Q14.6 29 15.3 33.3Z" fill="currentColor" />
      <path d="M14 26Q13.2 21.7 9.4 19.5Q10.2 23.8 14 26Z" fill="currentColor" />
      <path d="M14 26Q17.3 23.1 17.4 18.7Q14 21.6 14 26Z" fill="currentColor" />
      <path d="M14 18.7Q14 14.3 10.6 11.4Q10.7 15.8 14 18.7Z" fill="currentColor" />
      <path d="M14 18.7Q17.8 16.4 18.6 12.1Q14.8 14.4 14 18.7Z" fill="currentColor" />
      <path d="M15.3 11.3Q16.1 7 13.3 3.6Q12.6 7.9 15.3 11.3Z" fill="currentColor" />
      <path d="M15.3 11.3Q19.4 9.8 21 5.7Q16.9 7.2 15.3 11.3Z" fill="currentColor" />
    </svg>
  );
}

function WelcomeTrustBadge({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={compact ? "welcome-trust-badge welcome-trust-badge--compact" : "welcome-trust-badge"}
      aria-label="Trusted by 2,000 Mac users"
    >
      <WelcomeLaurel side="left" />
      <div className="welcome-trust-badge__inner">
        <div className="welcome-trust-badge__rating">
          <span className="welcome-trust-badge__rule" aria-hidden="true" />
          <span className="welcome-trust-badge__count">
            Trusted by <strong>2,000+</strong> Mac users
          </span>
          <span className="welcome-trust-badge__rule" aria-hidden="true" />
        </div>
        <div className="welcome-trust-badge__avatars" aria-hidden="true">
          {TRUST_AVATARS.map((avatar) => (
            <img key={avatar} src={avatar} alt="" loading="lazy" decoding="async" width={26} height={26} />
          ))}
        </div>
        <p>Private clipboard history for busy Mac users</p>
      </div>
      <WelcomeLaurel side="right" />
    </div>
  );
}

function WorkflowPotentialChart({ weeklyRange }: { weeklyRange: string }) {
  return (
    <figure
      className="workflow-potential-chart"
      aria-label="Chart comparing your potential with Linkhiv versus without Linkhiv over time"
    >
      <svg viewBox="0 0 390 248" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <line x1="52" y1="198" x2="52" y2="34" className="workflow-potential-chart__axis" />
        <polygon points="52,25 47,34 57,34" className="workflow-potential-chart__axis-fill" />
        <line x1="52" y1="198" x2="356" y2="198" className="workflow-potential-chart__axis" />
        <polygon points="366,198 356,193 356,203" className="workflow-potential-chart__axis-fill" />

        <text x="52" y="220" textAnchor="middle" className="workflow-potential-chart__axis-label">
          Today
        </text>
        <text x="356" y="220" textAnchor="end" className="workflow-potential-chart__axis-label">
          30 days
        </text>

        <path
          d="M52 198 C 118 196, 214 194, 356 186"
          className="workflow-potential-chart__line workflow-potential-chart__line--without"
        />
        <path
          d="M52 198 C 126 188, 216 150, 356 64"
          className="workflow-potential-chart__line workflow-potential-chart__line--with"
        />

        <circle cx="356" cy="186" r="4.5" className="workflow-potential-chart__dot workflow-potential-chart__dot--without" />
        <circle cx="356" cy="64" r="5.5" className="workflow-potential-chart__dot workflow-potential-chart__dot--with" />
        <circle cx="356" cy="64" r="12" className="workflow-potential-chart__halo" />

        <text x="336" y="177" textAnchor="end" className="workflow-potential-chart__label workflow-potential-chart__label--without">
          Without Linkhiv
        </text>
        <text x="336" y="56" textAnchor="end" className="workflow-potential-chart__label workflow-potential-chart__label--with">
          With Linkhiv
        </text>
        <text x="336" y="72" textAnchor="end" className="workflow-potential-chart__label-sub">
          {weeklyRange} reclaimed / week
        </text>
      </svg>
    </figure>
  );
}

function Icon({ name }: { name: "shield" | "bolt" | "desktop" | "check" | "spark" | "lock" }) {
  const shared = {
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true
  } as const;

  switch (name) {
    case "shield":
      return (
        <svg {...shared}>
          <path d="M12 3 18 5.5v5.7c0 3.8-2.55 7.25-6 8.3-3.45-1.05-6-4.5-6-8.3V5.5L12 3Z" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case "bolt":
      return (
        <svg {...shared}>
          <path d="M13 2 6.5 12H11l-1 10 7.5-11.5h-4L17 2h-4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
    case "desktop":
      return (
        <svg {...shared}>
          <rect x="4" y="5" width="16" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 19h6m-3-3v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case "check":
      return (
        <svg {...shared}>
          <path d="m6.5 12.2 3.2 3.2 7.8-7.8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "spark":
      return (
        <svg {...shared}>
          <path d="M12 4 13.8 8.2 18 10l-4.2 1.8L12 16l-1.8-4.2L6 10l4.2-1.8L12 4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
    case "lock":
      return (
        <svg {...shared}>
          <rect x="5.5" y="10" width="13" height="9" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8.5 10V8a3.5 3.5 0 0 1 7 0v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
  }
}

function OptionIcon({ name }: { name: Question["options"][number]["icon"] }) {
  const props = { size: 21, strokeWidth: 2 } as const;
  switch (name) {
    case "image":
      return <Image {...props} />;
    case "folder":
      return <Folder {...props} />;
    case "text":
      return <FileText {...props} />;
    case "mail":
      return <Mail {...props} />;
    case "search":
      return <Search {...props} />;
    case "layers":
      return <Layers3 {...props} />;
    case "sparkles":
      return <Sparkles {...props} />;
    case "shield":
      return <Shield {...props} />;
    case "zap":
      return <Zap {...props} />;
    case "clock":
      return <Clock3 {...props} />;
    case "desktop":
      return <Monitor {...props} />;
    case "briefcase":
      return <Briefcase {...props} />;
    case "users":
      return <Users {...props} />;
  }
}

function ProductPanel() {
  return (
    <div className="product-panel" aria-label="Linkhiv clipboard preview">
      <div className="panel-window hero-image-card">
        <img
          className="hero-image-card__image"
          src="/assets/hero/hero-graphic-flow-simple-508w.webp"
          srcSet="/assets/hero/hero-graphic-flow-simple-508w.webp 508w, /assets/hero/hero-graphic-flow-simple-1016w.webp 1016w"
          sizes="(max-width: 860px) 100vw, 508px"
          alt="Linkhiv product hero graphic showing clipboard recall"
          fetchPriority="high"
          decoding="async"
          width={1016}
          height={762}
        />
      </div>
    </div>
  );
}

// One image per feedback page — chosen to match the question's narrative.
// Each graphic is distinct from the welcome hero so nothing is reused.
const feedbackImageByQuestionId = {
  workflow_drag: {
    src: "/features-graphic.webp",
    alt: "Linkhiv keeping text, screenshots, and files together across apps"
  },
  privacy: {
    src: "/feature-highlight-graphic.webp",
    alt: "Linkhiv highlighting a recalled clip — kept locally on your Mac"
  }
} as const;

const checkoutAfterPaymentSteps = [
  {
    title: "Download instantly after checkout",
    subtitle: "The link is also emailed to you."
  },
  {
    title: "Install and activate with your email"
  },
  {
    title: "Open Linkhiv Clipboard history",
    subtitle: "Press Shift Command V or use the menu bar icon."
  }
] as const;

const getCheckoutAfterPaymentSteps = (checkoutOffer: CheckoutOffer) => {
  if (!isPolarTrialOffer(checkoutOffer)) {
    return checkoutAfterPaymentSteps;
  }

  return [
    {
      title: "Complete secure checkout",
      subtitle: "Use the email you want tied to your Linkhiv access."
    },
    {
      title: "Install and activate with your email",
      subtitle: "Your trial entitlement is checked when the app activates."
    },
    {
      title: "Trial reminder before renewal",
      subtitle: "We will send you a reminder 3 days before your trial ends.",
      icon: "bell"
    },
    {
      title: "Keep using Linkhiv after the trial",
      subtitle: "Your selected annual plan renews automatically unless canceled."
    }
  ] as const;
};

const getPaymentFaqs = (checkoutOffer: CheckoutOffer, prices?: StripePricesResponse | null) => {
  if (!isPolarTrialOffer(checkoutOffer)) {
    if (checkoutOffer.provider === "polar") {
      return paymentFaqs.map((faq) => {
        if (faq.question === "What happens after I pay?") {
          return {
            ...faq,
            answer: "Polar confirms your payment, then your download link and license key are sent to the email used at checkout."
          };
        }
        if (faq.question === "Is checkout secure?") {
          return {
            ...faq,
            answer: "Yes. Payment is handled securely by Polar, and Linkhiv does not store your card details."
          };
        }
        return faq;
      });
    }
    return paymentFaqs;
  }

  const standardRenewal = getTrialRenewalDisplay(checkoutOffer, "standard", prices);
  const proRenewal = getTrialRenewalDisplay(checkoutOffer, "pro", prices);

  return [
    {
      question: "Is this a subscription?",
      answer: `Yes. This test offer starts with a free ${checkoutOffer.trialDays}-day trial. Standard renews at ${standardRenewal} and Pro renews at ${proRenewal} unless canceled.`
    },
    {
      question: "What happens after checkout?",
      answer:
        "Checkout confirms your trial, then the app can activate using the email used at checkout."
    },
    {
      question: "What if it is not right for me?",
      answer:
        "Cancel before renewal from your customer portal. We will also send a reminder 3 days before your trial ends."
    },
    {
      question: "Does Linkhiv upload my clipboard history?",
      answer:
        "No. Your copied text, screenshots, files, and links stay on your Mac. Linkhiv does not upload them to a cloud workspace."
    },
    {
      question: "Is checkout secure?",
      answer: "Yes. Checkout is handled securely by Polar (payments are processed through Stripe), and Linkhiv does not store your card details."
    }
  ] as const;
};

function FeedbackHeroImage({ questionId }: { questionId: string | null }) {
  const image =
    (questionId ? feedbackImageByQuestionId[questionId as keyof typeof feedbackImageByQuestionId] : null) ??
    feedbackImageByQuestionId.workflow_drag;

  return (
    <figure className="feedback-hero-image" aria-label="Linkhiv visual preview">
      <img src={image.src} alt={image.alt} loading="eager" />
    </figure>
  );
}

function SocialProofScroller() {
  return (
    <div className="testimonial-stack" aria-label="Customer reviews">
      {testimonials.map((testimonial) => (
        <article key={`${testimonial.author}-${testimonial.role}`}>
          <p>"{testimonial.quote}"</p>
          <div>
            <img src={testimonial.image} alt="" loading="lazy" decoding="async" width={32} height={32} />
            <span>{testimonial.author}<small>{testimonial.role}</small></span>
          </div>
        </article>
      ))}
    </div>
  );
}

function CheckoutComplete() {
  const [message, setMessage] = useState("Confirming your Linkhiv checkout...");

  useEffect(() => {
    initAnalytics();
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId && window.location.hash !== "#checkout_complete") {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#checkout_complete`
      );
    }
    captureCheckoutCompletePageview({
      includeEmailStep: shouldShowEmailStepFromLocation(window.location)
    });

    const token = sessionStorage.getItem(CHECKOUT_TOKEN_KEY) ?? undefined;

    if (!sessionId) {
      setMessage("Checkout complete. Keep an eye on your inbox for Linkhiv access.");
      return;
    }

    // The `_fbp` cookie is reliably set by now, so re-send Meta identifiers to backfill the
    // server-side Purchase when the earlier createSession snapshot missed them (lifts EMQ).
    const completeTracking = hasPrivacyOptOutSignal()
      ? undefined
      : (() => {
          const t = getTrackingContext();
          return {
            fbp: t.fbp ?? undefined,
            fbc: t.fbc ?? undefined,
            externalId: t.externalId
          };
        })();

    completeCheckoutSession(sessionId, token, completeTracking)
      .then((result) => {
        sessionStorage.removeItem(CHECKOUT_TOKEN_KEY);
        const pending = getPendingCheckoutTracking(sessionId);
        if (pending) {
          trackMetaPurchase(
            result.metaEventId ?? pending.eventId,
            result.planId,
            result.purchaseValue ?? pending.value,
            result.currency ?? pending.currency
          );
        }
        track("checkout_complete", {
          session_id: sessionId,
          plan_id: result.planId,
          payment_status: result.paymentStatus,
          email_sent: result.emailSent
        });
        setMessage(
          result.emailSent
            ? "You're in. We sent your Linkhiv access details to your checkout email."
            : "You're in. Your payment is confirmed, and Linkhiv access will follow by email."
        );
      })
      .catch(() => {
        setMessage("Payment received. We could not verify the session in-browser, but your Stripe confirmation is the source of truth.");
      });
  }, []);

  return (
    <main className="complete-shell">
      <section className="complete-card">
        <img src={brand.iconPath} alt="" />
        <p className="eyebrow">Checkout complete</p>
        <h1>Welcome to Linkhiv.</h1>
        <p>{message}</p>
        <a className="button button-primary" href="/">
          Back to funnel
        </a>
      </section>
    </main>
  );
}

export default function App() {
  const [landingAttribution] = useState(() => getOrCreateLandingAttribution());
  const [entryConcept] = useState<FunnelConcept>(() => getInitialEntryConcept());
  const [showDedicatedEmailStep] = useState<boolean>(() =>
    typeof window !== "undefined" ? shouldShowEmailStepFromLocation(window.location) : false
  );
  const [checkoutOffer] = useState<CheckoutOffer>(() =>
    typeof window !== "undefined" ? getCheckoutOfferFromLocation(window.location) : defaultCheckoutOffer
  );
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [email, setEmail] = useState("");
  const [leadId, setLeadId] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanId>("pro");
  const [pendingFeedbackQuestionId, setPendingFeedbackQuestionId] = useState<string | null>(null);
  const [prices, setPrices] = useState<StripePricesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [offerSecondsLeft, setOfferSecondsLeft] = useState(14 * 60 + 59);
  const [showFloatingCheckoutCta, setShowFloatingCheckoutCta] = useState(false);
  const lastCapturedLeadEmailRef = useRef<string | null>(null);
  const emailCaptureInFlightRef = useRef<string | null>(null);
  const [expandedMobilePlanId, setExpandedMobilePlanId] = useState<PlanId | null>(null);
  const [showPlanComparison, setShowPlanComparison] = useState(false);

  const isCompleteRoute = window.location.hash === "#checkout_complete" || window.location.search.includes("session_id=");
  const resultStep = questions.length + 1;
  const emailStep = resultStep + 1;
  const paymentStep = getPaymentStep(showDedicatedEmailStep);
  const showPayment = step >= paymentStep;
  const progress = Math.min(100, Math.round((step / paymentStep) * 100));
  const recommendedPlan = useMemo(() => getRecommendedPlan(answers), [answers]);
  const isTrialCheckoutOffer = isPolarTrialOffer(checkoutOffer);
  const displayCheckoutPlans = useMemo(
    () => getDisplayCheckoutPlans(checkoutOffer, prices),
    [checkoutOffer, prices]
  );
  const activePaymentFaqs = useMemo(() => getPaymentFaqs(checkoutOffer, prices), [checkoutOffer, prices]);
  const activeCheckoutAfterPaymentSteps = useMemo(() => getCheckoutAfterPaymentSteps(checkoutOffer), [checkoutOffer]);
  const activeValueChips = isTrialCheckoutOffer
    ? (["Private by design", "Native to macOS", `${checkoutOffer.trialDays}-day trial`, "Screenshots included"] as const)
    : valueChips;
  const showPlanComparisonToggle = displayCheckoutPlans.length > 1;
  const activeSelectedPlan = displayCheckoutPlans.some((plan) => plan.id === selectedPlan)
    ? selectedPlan
    : displayCheckoutPlans[0]?.id ?? "pro";
  const summary = useMemo(() => summarizeAnswers(answers), [answers]);
  const resultInsights = useMemo(() => getResultInsights(answers), [answers]);
  const pendingFeedback = useMemo(
    () => getPendingFeedbackContent(pendingFeedbackQuestionId, answers),
    [answers, pendingFeedbackQuestionId]
  );
  const diagnosisSignals = [
    {
      key: "lost",
      label: "Most likely lost",
      value: resultInsights.lostItem,
      toneClassName: "result-summary-row--lost"
    },
    {
      key: "pattern",
      label: "Shows up",
      value: resultInsights.recallPattern,
      toneClassName: "result-summary-row--pattern"
    },
    {
      key: "drag",
      label: "Biggest drag",
      value: resultInsights.friction,
      toneClassName: "result-summary-row--drag"
    },
    {
      key: "feel",
      label: "Needs to feel",
      value: resultInsights.privacySignal,
      toneClassName: "result-summary-row--feel"
    }
  ] as const;
  const isLocalDev = import.meta.env.DEV;
  const selectedPlanData = findDisplayCheckoutPlan(displayCheckoutPlans, activeSelectedPlan);
  const selectedPlanPrice = !isTrialCheckoutOffer && prices?.[activeSelectedPlan]
    ? money(prices[activeSelectedPlan].unitAmount, prices[activeSelectedPlan].currency)
    : selectedPlanData.price;
  const selectedPlanCtaPrice = isTrialCheckoutOffer ? selectedPlanPrice : formatCheckoutCtaPrice(selectedPlanPrice);
  const selectedPlanCtaLabel = isLoading
    ? "Opening checkout..."
    : isTrialCheckoutOffer
      ? checkoutOffer.priceAmount <= 0
        ? `Start ${checkoutOffer.trialDays}-day free trial`
        : `Start ${checkoutOffer.trialDays}-day trial for ${selectedPlanCtaPrice}`
      : `Get Linkhiv ${selectedPlanData.name} for ${selectedPlanCtaPrice}`;
  const paymentEyebrow = isTrialCheckoutOffer ? `${checkoutOffer.trialDays}-day Linkhiv trial` : "One-time Linkhiv access";
  const paymentTitle = isTrialCheckoutOffer ? "Your Linkhiv trial is ready" : "Your Linkhiv setup is ready";
  const paymentDescription = isTrialCheckoutOffer
    ? `${getTrialIntroCopy(checkoutOffer)} Standard renews at ${getTrialRenewalDisplay(checkoutOffer, "standard", prices)} and Pro renews at ${getTrialRenewalDisplay(checkoutOffer, "pro", prices)} unless canceled.`
    : "Your clipboard history stays on your Mac. Linkhiv does not upload copied text, screenshots, files, or links to the cloud.";
  const purchaseReassuranceLabel = isTrialCheckoutOffer
    ? "Free trial"
    : "One-time purchase";
  const checkoutSecureLabel = checkoutOffer.provider === "polar" ? "Secure checkout" : "Stripe secure";
  const sideCtaNote = isTrialCheckoutOffer
    ? `Secure checkout · Free trial · ${getTrialRenewalDisplay(checkoutOffer, activeSelectedPlan, prices)} after trial`
    : `${checkoutOffer.provider === "polar" ? "Secure checkout" : "Secure Stripe checkout"} · Pay once · Use forever`;
  const floatingCtaTrust = isTrialCheckoutOffer
    ? `Free trial · ${getTrialRenewalDisplay(checkoutOffer, activeSelectedPlan, prices)} after trial · Secure checkout`
    : `Pay once · 30-day guarantee · ${checkoutOffer.provider === "polar" ? "Secure checkout" : "Secure Stripe checkout"}`;
  const emailContinueLabel = isTrialCheckoutOffer
    ? `Unlock my ${checkoutOffer.trialDays}-day trial`
    : "Unlock my one-time offer";
  const resultRecommendationTitle = isTrialCheckoutOffer
    ? `${recommendedPlan === "standard" ? "Standard" : "Pro"} Trial is your match.`
    : recommendedPlan === "standard" ? "Standard is your match." : "Pro is your match.";
  const resultRecommendationCopy = isTrialCheckoutOffer
    ? `${getTrialIntroCopy(checkoutOffer)} ${getTrialRenewalCopy(checkoutOffer, recommendedPlan, prices)}`
    : recommendedPlan === "standard"
      ? "A full Linkhiv license for one Mac, with every capture type included."
      : "Five devices covered for just $10 more than Standard, with the full feature set and email support.";
  const pageUrl = typeof window === "undefined" ? undefined : window.location.href;
  const normalizedEmail = normalizeEmail(email);
  const hasValidCheckoutEmail = isValidEmail(normalizedEmail);
  const showInlineEmailValidation = showPayment && !showDedicatedEmailStep && email.trim().length > 0 && !hasValidCheckoutEmail;
  const normalizeStepForFlow = useCallback(
    (candidateStep: number): number => {
      const nextStep = Number.isFinite(candidateStep) ? Math.max(0, candidateStep) : 0;
      if (showDedicatedEmailStep) return nextStep;
      return nextStep >= emailStep ? paymentStep : nextStep;
    },
    [emailStep, paymentStep, showDedicatedEmailStep]
  );

  useEffect(() => {
    initAnalytics();
    track("funnel_loaded", {
      entry_concept: entryConcept.key,
      entry_concept_label: entryConcept.label,
      checkout_offer: checkoutOffer.analyticsKey,
      checkout_provider: checkoutOffer.provider
    });
  }, [entryConcept.key, entryConcept.label, checkoutOffer.analyticsKey, checkoutOffer.provider]);

  // Meta PageView: browser Pixel + CAPI share `pv-<uuid>` so Events Manager dedups them.
  // Claim the session flag synchronously (before any await) to prevent duplicate fires from
  // React StrictMode's double mount in dev.
  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    if (hasPrivacyOptOutSignal()) return;
    if (sessionStorage.getItem(META_PAGE_VIEW_SESSION_KEY) === "1") return;
    sessionStorage.setItem(META_PAGE_VIEW_SESSION_KEY, "1");

    const tracking = getTrackingContext();
    const pageViewEventId = `pv-${tracking.eventId}`;

    firePageViewBrowser(pageViewEventId);

    if (!config.apiBaseUrl) return;

    // Wait briefly so `fbevents.js` has time to set `_fbp` — this is the only event that
    // races with Pixel init; later events fire after substantial user interaction.
    void waitForFbp().then(() => {
      const fresh = getTrackingContext();
      void trackPageViewServerEvent({
        eventId: pageViewEventId,
        fbp: fresh.fbp,
        fbc: fresh.fbc,
        externalId: fresh.externalId,
        pageUrl: typeof window === "undefined" ? undefined : window.location.href
      }).catch((e) => console.warn("[meta] PageView CAPI track failed", e));
    });
  }, []);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as StoredFunnelState;
        setStep(typeof parsed.step === "number" ? normalizeStepForFlow(parsed.step) : 0);
        setAnswers(parsed.answers ?? {});
        setEmail(parsed.email ?? "");
        setLeadId(parsed.leadId ?? null);
        setPendingFeedbackQuestionId(parsed.pendingFeedbackQuestionId ?? null);
        if (parsed.selectedPlan === "standard" || parsed.selectedPlan === "pro" || parsed.selectedPlan === "unlimited") {
          setSelectedPlan(parsed.selectedPlan);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    if (window.location.hash !== "#checkout_complete") {
      const hashScreen = parseHashToScreen(window.location.hash, {
        includeEmailStep: showDedicatedEmailStep
      });
      if (hashScreen) {
        setStep(normalizeStepForFlow(hashScreen.step));
        setPendingFeedbackQuestionId(hashScreen.pendingFeedbackQuestionId);
      }
    }
  }, [normalizeStepForFlow, showDedicatedEmailStep]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        step,
        answers,
        email,
        selectedPlan,
        leadId,
        pendingFeedbackQuestionId,
        entryConceptKey: entryConcept.key,
        lastScreenHash: getHashForScreen(step, pendingFeedbackQuestionId, {
          includeEmailStep: showDedicatedEmailStep
        })
      })
    );
  }, [step, answers, email, selectedPlan, leadId, pendingFeedbackQuestionId, entryConcept.key, showDedicatedEmailStep]);

  useEffect(() => {
    if (!config.apiBaseUrl) return;
    fetchPrices(checkoutOffer.provider === "polar" ? checkoutOffer.id : undefined)
      .then(setPrices)
      .catch(() => {
        // Static fallback prices remain visible.
      });
  }, [checkoutOffer.id, checkoutOffer.provider]);

  useEffect(() => {
    registerFunnelPersonProperties({
      recommended_plan: recommendedPlan,
      entry_concept: entryConcept.key,
      entry_concept_label: entryConcept.label,
      checkout_offer: checkoutOffer.analyticsKey,
      checkout_provider: checkoutOffer.provider
    });
  }, [recommendedPlan, entryConcept.key, entryConcept.label, checkoutOffer.analyticsKey, checkoutOffer.provider]);

  useEffect(() => {
    captureFunnelScreenPageview(step, pendingFeedbackQuestionId, {
      recommendedPlan,
      entryConceptKey: entryConcept.key,
      entryConceptLabel: entryConcept.label
    }, {
      includeEmailStep: showDedicatedEmailStep
    });
  }, [pendingFeedbackQuestionId, recommendedPlan, step, entryConcept.key, entryConcept.label, showDedicatedEmailStep]);

  useEffect(() => {
    const screenName = getFunnelScreenName(step, pendingFeedbackQuestionId, {
      includeEmailStep: showDedicatedEmailStep
    });

    track("funnel_step_viewed", {
      step,
      screen_name: screenName,
      recommended_plan: recommendedPlan,
      entry_concept: entryConcept.key,
      entry_concept_label: entryConcept.label
    });
  }, [pendingFeedbackQuestionId, recommendedPlan, step, entryConcept.key, entryConcept.label, showDedicatedEmailStep]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setOfferSecondsLeft((current) => (current <= 0 ? 0 : current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isCompleteRoute) return;
    const nextHash = getHashForScreen(step, pendingFeedbackQuestionId, {
      includeEmailStep: showDedicatedEmailStep
    });
    if (window.location.hash !== nextHash) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}${nextHash}`
      );
    }
  }, [isCompleteRoute, pendingFeedbackQuestionId, showDedicatedEmailStep, step]);

  useEffect(() => {
    if (isCompleteRoute) return;
    const onHashChange = () => {
      const hashScreen = parseHashToScreen(window.location.hash, {
        includeEmailStep: showDedicatedEmailStep
      });
      if (!hashScreen) return;
      setStep(normalizeStepForFlow(hashScreen.step));
      setPendingFeedbackQuestionId(hashScreen.pendingFeedbackQuestionId);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [isCompleteRoute, normalizeStepForFlow, showDedicatedEmailStep]);

  useEffect(() => {
    if (!showPayment) {
      setShowFloatingCheckoutCta(false);
      return;
    }

    const mobileMedia = window.matchMedia("(max-width: 1120px)");

    const syncFloatingCheckout = () => setShowFloatingCheckoutCta(mobileMedia.matches);
    syncFloatingCheckout();

    mobileMedia.addEventListener("change", syncFloatingCheckout);
    return () => {
      mobileMedia.removeEventListener("change", syncFloatingCheckout);
    };
  }, [showPayment]);

  if (isCompleteRoute) {
    return <CheckoutComplete />;
  }

  const showResult = step === resultStep;
  const showEmail = showDedicatedEmailStep && step === emailStep;
  const currentQuestion = pendingFeedback ? undefined : questions[step - 1];
  const showWelcome = step === 0;
  const canGoBack = step > 0 || Boolean(pendingFeedback);

  const goBack = () => {
    setError("");
    if (pendingFeedback) {
      setPendingFeedbackQuestionId(null);
      return;
    }
    setStep((current) => Math.max(0, current - 1));
  };

  const advanceFromWelcome = useCallback(() => {
    if (
      !hasPrivacyOptOutSignal() &&
      typeof sessionStorage !== "undefined" &&
      !sessionStorage.getItem(META_VIEW_CONTENT_SESSION_KEY)
    ) {
      const tracking = getTrackingContext();
      const plan = isTrialCheckoutOffer ? "pro" : getRecommendedPlan(answers);
      const live = prices?.[plan];
      const staticPlan = plans.find((p) => p.id === plan);
      const value = isTrialCheckoutOffer
        ? checkoutOffer.priceAmount
        : live
          ? live.unitAmount / 100
          : Number(staticPlan?.price.replace(/[^0-9.]/g, "") ?? 0);
      const currency = (isTrialCheckoutOffer ? live?.currency ?? checkoutOffer.currency : live?.currency ?? "USD").toUpperCase();
      const contentName = isTrialCheckoutOffer ? "Linkhiv Pro Trial" : `Linkhiv ${staticPlan?.name ?? plan}`;
      const viewContentEventId = `vc-${tracking.eventId}`;
      trackMetaViewContent({
        eventId: viewContentEventId,
        contentIds: [plan],
        value,
        currency,
        contentName
      });
      void trackViewContentServerEvent({
        eventId: viewContentEventId,
        contentIds: [plan],
        value,
        currency,
        contentName,
        fbp: tracking.fbp,
        fbc: tracking.fbc,
        externalId: tracking.externalId,
        pageUrl
      }).catch((e) => console.warn("[meta] ViewContent CAPI track failed", e));
      sessionStorage.setItem(META_VIEW_CONTENT_SESSION_KEY, "1");
    }
    setStep(1);
  }, [answers, checkoutOffer.currency, checkoutOffer.priceAmount, isTrialCheckoutOffer, pageUrl, prices]);

  const selectAnswer = (questionId: string, optionId: string) => {
    setAnswers((current) => ({ ...current, [questionId]: optionId }));
    setError("");
    track("quiz_option_selected", {
      question_id: questionId,
      option_id: optionId,
      step
    });
    if (feedbackByQuestionId[questionId]) {
      window.setTimeout(() => setPendingFeedbackQuestionId(questionId), 140);
      return;
    }
    window.setTimeout(() => setStep((current) => current + 1), 160);
  };

  const captureEmailLead = useCallback(
    (rawEmail: string): boolean => {
      const nextEmail = normalizeEmail(rawEmail);
      if (!config.enableEmailCapture || !isValidEmail(nextEmail)) return false;
      if (!config.apiBaseUrl) {
        console.warn("[leads] Email capture skipped because VITE_API_BASE_URL is not configured.");
        return false;
      }
      if (
        lastCapturedLeadEmailRef.current === nextEmail ||
        emailCaptureInFlightRef.current === nextEmail
      ) {
        return true;
      }

      emailCaptureInFlightRef.current = nextEmail;
      const leadTracking = getTrackingContext();
      const leadEventId = `lead-${leadTracking.eventId}`;
      const leadMetaSessionKey = `${META_LEAD_SESSION_PREFIX}${nextEmail}`;
      const shouldTrackLeadMetaEvent =
        !hasPrivacyOptOutSignal() &&
        (typeof sessionStorage === "undefined" || sessionStorage.getItem(leadMetaSessionKey) !== "1");

      void (async () => {
        try {
          const leadTrackPromise = shouldTrackLeadMetaEvent
            ? trackLeadServerEvent({
                email: nextEmail,
                eventId: leadEventId,
                fbp: leadTracking.fbp,
                fbc: leadTracking.fbc,
                externalId: leadTracking.externalId,
                pageUrl
              })
            : Promise.resolve();
          const [leadSaveResult, leadTrackResult] = await Promise.allSettled([
            captureLead({
              email: nextEmail,
              onboardingResponses: { answers, recommendedPlan },
              source: "linkhiv_webfunnel",
              attribution: landingAttribution
            }),
            leadTrackPromise
          ]);

          if (shouldTrackLeadMetaEvent && leadTrackResult.status === "rejected") {
            console.warn("[leads] server-side lead CAPI track failed", leadTrackResult.reason);
          }

          if (leadSaveResult.status === "fulfilled") {
            lastCapturedLeadEmailRef.current = nextEmail;
            setLeadId(leadSaveResult.value.leadId);
            if (typeof leadSaveResult.value.leadId === "number") {
              // Promote anon-<uuid> -> lead-<id> so subsequent IC + Purchase share a cross-session identifier.
              upgradeExternalIdToLead(leadSaveResult.value.leadId);
            }
            track("email_captured", {
              lead_id: leadSaveResult.value.leadId,
              recommended_plan: recommendedPlan
            });
          } else {
            const leadError = leadSaveResult.reason;
            track("email_capture_failed", {
              recommended_plan: recommendedPlan,
              message: leadError instanceof Error ? leadError.message : "unknown"
            });
            console.warn("[leads] failed to save lead", leadError);
          }

          if (shouldTrackLeadMetaEvent) {
            trackMetaLead(leadEventId, { email: nextEmail });
            if (typeof sessionStorage !== "undefined") {
              sessionStorage.setItem(leadMetaSessionKey, "1");
            }
          }
        } finally {
          if (emailCaptureInFlightRef.current === nextEmail) {
            emailCaptureInFlightRef.current = null;
          }
        }
      })();

      return true;
    },
    [answers, landingAttribution, pageUrl, recommendedPlan]
  );

  const advanceToPayment = useCallback(() => {
    setError("");
    setStep(paymentStep);
  }, [paymentStep]);

  const advanceFromResult = useCallback(() => {
    setError("");
    setStep(showDedicatedEmailStep ? emailStep : paymentStep);
  }, [emailStep, paymentStep, showDedicatedEmailStep]);

  const togglePlanDetails = useCallback((planId: PlanId) => {
    setExpandedMobilePlanId((current) => (current === planId ? null : planId));
  }, []);

  const saveEmailAndContinue = () => {
    setError("");
    if (!hasValidCheckoutEmail) {
      setError("Enter a valid email for your download and activation details.");
      return;
    }
    captureEmailLead(normalizedEmail);
    advanceToPayment();
  };

  const startCheckout = async (planId: PlanId) => {
    setError("");
    setSelectedPlan(planId);

    if (!config.apiBaseUrl) {
      setError("Checkout worker URL is not configured yet. Set VITE_API_BASE_URL when the Linkhiv worker is deployed.");
      return;
    }

    try {
      setIsLoading(true);
      const staticPlan = findCheckoutPlan(planId);
      const price = prices?.[planId];
      const privacyOptedOut = hasPrivacyOptOutSignal();
      const tracking = getTrackingContext();
      const fbp = !privacyOptedOut ? (await waitForFbp(CHECKOUT_FBP_WAIT_MS)) ?? tracking.fbp : null;
      if (hasValidCheckoutEmail) {
        captureEmailLead(normalizedEmail);
      }
      const value = isTrialCheckoutOffer
        ? checkoutOffer.priceAmount
        : price
          ? price.unitAmount / 100
          : Number(staticPlan?.price.replace(/[^0-9.]/g, "") ?? 0);
      const currency = isTrialCheckoutOffer ? price?.currency ?? checkoutOffer.currency : price?.currency ?? "USD";
      track("checkout_started", {
        plan_id: planId,
        checkout_offer: checkoutOffer.analyticsKey,
        checkout_provider: checkoutOffer.provider,
        value,
        currency
      });
      if (!privacyOptedOut) {
        trackMetaInitiateCheckout(tracking.eventId, planId, value, currency);
      }
      const response = await createCheckoutSession({
        email: hasValidCheckoutEmail ? normalizedEmail : undefined,
        leadId: leadId ?? undefined,
        planId,
        flow: "a",
        offer: checkoutOffer.provider === "polar" ? checkoutOffer.id : undefined,
        currency,
        value,
        eventId: privacyOptedOut ? undefined : tracking.eventId,
        fbp: privacyOptedOut ? undefined : fbp ?? undefined,
        fbc: privacyOptedOut ? undefined : tracking.fbc ?? undefined,
        externalId: privacyOptedOut ? undefined : tracking.externalId,
        pageUrl: privacyOptedOut ? undefined : pageUrl,
        attribution: landingAttribution
      });

      track(checkoutOffer.provider === "polar" ? "checkout_redirect_to_polar" : "checkout_redirect_to_stripe", {
        plan_id: planId,
        checkout_offer: checkoutOffer.analyticsKey,
        checkout_provider: checkoutOffer.provider,
        checkout_session_id: response.sessionId,
        value,
        currency
      });

      sessionStorage.setItem(CHECKOUT_TOKEN_KEY, response.completionToken);
      if (!privacyOptedOut) {
        rememberPendingCheckoutTracking({
          sessionId: response.sessionId,
          eventId: tracking.eventId,
          planId,
          value,
          currency,
          completionToken: response.completionToken
        });
      }
      window.location.assign(response.checkoutUrl);
    } catch (checkoutError) {
      track("checkout_failed", {
        plan_id: planId,
        checkout_offer: checkoutOffer.analyticsKey,
        checkout_provider: checkoutOffer.provider,
        message: checkoutError instanceof Error ? checkoutError.message : "Checkout could not be started."
      });
      setError(checkoutError instanceof Error ? checkoutError.message : "Checkout could not be started.");
    } finally {
      setIsLoading(false);
    }
  };

  const offerMinutes = String(Math.floor(offerSecondsLeft / 60)).padStart(2, "0");
  const offerSeconds = String(offerSecondsLeft % 60).padStart(2, "0");

  return (
    <div className="page-shell">
      <header className="funnel-header">
        {canGoBack ? (
          <button
            className="top-back"
            type="button"
            onClick={goBack}
            aria-label="Go back"
          >
            <ArrowLeft size={18} strokeWidth={2.4} />
          </button>
        ) : (
          <div className="top-back-spacer" aria-hidden="true" />
        )}
        <a className="brand-lockup" href="/" aria-label="Linkhiv home">
          <img
            className="brand-lockup__icon"
            src={brand.iconPath}
            width={28}
            height={28}
            alt=""
          />
          <span className="brand-lockup__text">Linkhiv</span>
        </a>
        <div className="top-back-spacer" />
      </header>

      <main className={`funnel-main ${showPayment ? "funnel-main--payment" : ""}`}>
        <section
          className={`funnel-card ${pendingFeedback || showResult ? "funnel-card--sticky-cta" : ""}`}
        >
          <div className="progress-track" aria-hidden="true">
            <span style={{ width: `${progress}%` }} />
          </div>

          {showWelcome ? (
            <div className="hero-step hero-step--tap-advance" onClick={advanceFromWelcome}>
              <div className="hero-step__intro">
                <div className="hero-chip">
                  <img src="/assets/apple-32w.webp" width={16} height={16} alt="" />
                  Built for macOS
                </div>
                <h1 className={entryConcept.heroTitleLayout === "flow" ? "hero-title-flow" : undefined}>
                  <span className="hero-title-main">{entryConcept.heroTitleMain}</span>
                  <span className="hero-title-accent">{entryConcept.heroTitleAccent}</span>
                </h1>
                <p className="hero-subtitle">
                  {entryConcept.heroSubtitle}
                </p>
              </div>
              <div className="hero-step__media">
                <ProductPanel />
              </div>
              <div className="hero-step__cta">
                <div className="hero-actions">
                  <div className="hero-actions__cta">
                    <button className="button button-primary checkout-cta-button" type="button">
                      Fix my Mac clipboard
                    </button>
                    <p className="welcome-cta-friction">30 seconds · No spam · Instant access</p>
                    <WelcomeTrustBadge />
                  </div>
                </div>
                <div className="welcome-benefits" aria-label="Why Linkhiv converts">
                  <span><Icon name="spark" /> Organizes text, screenshots, files, and folders</span>
                  <span><Icon name="bolt" /> Find what you copied without reopening tabs</span>
                  <span><Icon name="lock" /> Local-only on your Mac. {purchaseReassuranceLabel}.</span>
                </div>
              </div>
            </div>
          ) : null}

          {pendingFeedback ? (
            <div className="feedback-step">
              <div className="feedback-step__layout">
                <div className="feedback-step__content">
                  <h2>
                    {pendingFeedback.highlightedTitle ? (
                      <>
                        {pendingFeedback.title}{" "}
                        <span className="feedback-step__highlight">{pendingFeedback.highlightedTitle}</span>{" "}
                        are exactly what Linkhiv helps with.
                      </>
                    ) : (
                      pendingFeedback.title
                    )}
                  </h2>
                  <p>{pendingFeedback.description}</p>
                  <div className="feedback-bullets">
                    {pendingFeedback.bullets.map((bullet) => (
                      <span key={bullet}><Icon name="check" /> {bullet}</span>
                    ))}
                  </div>
                </div>
                <div className="feedback-step__media feedback-step__media--single">
                  <FeedbackHeroImage questionId={pendingFeedbackQuestionId} />
                </div>
              </div>
            </div>
          ) : null}

          {currentQuestion ? (
            <div className="question-step">
              <p className="eyebrow">{currentQuestion.eyebrow}</p>
              <h2>{currentQuestion.title}</h2>
              <p>{currentQuestion.subtitle}</p>
              <div className="option-grid">
                {currentQuestion.options.map((option) => (
                  <button
                    className={`option-card tone-${option.tone}`}
                    type="button"
                    key={option.id}
                    onClick={() => selectAnswer(currentQuestion.id, option.id)}
                  >
                    <span className="option-dot">
                      <OptionIcon name={option.icon} />
                    </span>
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {showResult ? (
            <div className="result-step">
              <div className="performance-card performance-card--primary" aria-label="Estimated weekly time reclaimed">
                <div className="performance-card__topline">
                  <span>You have great potential</span>
                  <strong>{resultInsights.weeklyRange}</strong>
                  <small>you could reclaim each week with calmer recall</small>
                </div>
                <WorkflowPotentialChart weeklyRange={resultInsights.weeklyRange} />
                <p>
                  Based on reported productivity gains from Linkhiv users with repeat copy-and-paste workflows.
                </p>
              </div>

              <section className="result-recommendation" aria-label="Recommended Linkhiv plan">
                <div className="insight-panel insight-panel--recommended">
                  <img src={brand.iconPath} alt="" />
                  <p className="eyebrow">Recommended plan</p>
                  <strong>{resultRecommendationTitle}</strong>
                  <p>{resultRecommendationCopy}</p>
                  <div className="result-recommendation__trust">
                    <div className="result-recommendation__points" aria-label="Purchase reassurance">
                      <span><Icon name="check" /> {purchaseReassuranceLabel}</span>
                      <span><Icon name="shield" /> Private on your Mac</span>
                    </div>
                    <WelcomeTrustBadge compact />
                  </div>
                </div>
              </section>

              <section className="result-diagnosis" aria-label="Personalized workflow diagnosis">
                <div className="result-diagnosis__copy">
                  <p className="eyebrow result-diagnosis__eyebrow">Your workflow diagnosis</p>
                  <h3>You are losing time in repeat recall.</h3>
                  <p>
                    Based on your answers, Linkhiv should matter most when {resultInsights.lostItem} need to come back
                    quickly and the biggest drag is {resultInsights.friction.toLowerCase()}. Because{" "}
                    {resultInsights.privacySignal}, the right fix needs to feel instant while staying on your Mac.
                  </p>
                  <div className="result-diagnosis__summary" aria-label="Key diagnosis signals">
                    {diagnosisSignals.map((signal) => (
                      <div className={`result-summary-row ${signal.toneClassName}`} key={signal.key}>
                        <span className="result-summary-row__label">{signal.label}</span>
                        <strong className="result-summary-row__value">{signal.value}</strong>
                      </div>
                    ))}
                  </div>
                  <p className="result-diagnosis__footer">
                    That makes Linkhiv a strong fit for a calmer, more trustworthy Mac workflow.
                  </p>
                </div>
              </section>
            </div>
          ) : null}

          {showEmail ? (
            <div className="email-step">
              <p className="eyebrow">One last thing</p>
              <h2>Where should we send your activation details?</h2>
              <p className="email-step__support">
                We&apos;ll send your download link and activation details here. Use an inbox you actually check.
              </p>
              <label className="email-field">
                <input
                  type="email"
                  value={email}
                  placeholder="Enter your best email"
                  inputMode="email"
                  autoComplete="email"
                  aria-label="Email address"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              <div className="email-step__continue-wrap">
                <button className="button button-primary" type="button" disabled={isLoading} onClick={saveEmailAndContinue}>
                  {isLoading ? "Unlocking..." : emailContinueLabel}
                </button>
                <div className="email-reassurance" aria-label="Why your email is safe">
                  <span><Icon name="lock" /> Helpful resources, no spam.</span>
                  <span><Icon name="check" /> {purchaseReassuranceLabel}.</span>
                </div>
                <LegalAgreementNote />
              </div>
              <section className="email-social-proof" aria-label="Customer reviews before checkout">
                <div className="email-social-proof__badge">
                  <WelcomeTrustBadge compact />
                </div>
                <SocialProofScroller />
              </section>
              {isLocalDev ? (
                <button className="text-button" type="button" onClick={() => setStep((current) => current + 1)}>
                  Skip for now
                </button>
              ) : null}
            </div>
          ) : null}

          {showPayment ? (
            <div className="payment-step" id="pricing">
              <div className="section-intro">
                <p className="eyebrow">{paymentEyebrow}</p>
                <h2>{paymentTitle}</h2>
                <p>{paymentDescription}</p>
              </div>
              <figure className="checkout-product-demo">
                <img
                  src="/assets/hero/linkhiv-checkout-product-demo.webp"
                  alt="Linkhiv clipboard and screenshot history product demo with shortcuts, preview, metadata editing, and item deletion"
                  loading="eager"
                  decoding="async"
                  width={1536}
                  height={1024}
                />
              </figure>
              <div className="plan-grid">
                {displayCheckoutPlans.map((plan) => {
                  const livePrice = prices?.[plan.id];
                  const displayPrice =
                    isTrialCheckoutOffer
                      ? plan.price
                      : livePrice
                        ? money(livePrice.unitAmount, livePrice.currency)
                        : plan.price;
                  const renewalPrice = isTrialCheckoutOffer
                    ? getTrialRenewalDisplay(checkoutOffer, plan.id, prices)
                    : null;
                  const isExpanded = expandedMobilePlanId === plan.id;
                  const planDetailsId = `plan-details-${plan.id}`;

                  return (
                    <article
                      className={`plan-card ${activeSelectedPlan === plan.id ? "is-selected" : ""} ${isExpanded ? "is-expanded" : ""} ${!showPlanComparison && activeSelectedPlan !== plan.id ? "plan-card--mobile-hidden" : ""}`}
                      key={plan.id}
                      onClick={() => {
                        setSelectedPlan(plan.id);
                      }}
                    >
                      {plan.badge ? (
                        <span
                          className={`plan-badge plan-badge--${plan.badgeVariant ?? "primary"}`}
                        >
                          {plan.badge}
                        </span>
                      ) : null}
                      <div className="plan-card__header">
                        <p className="plan-card__detail">{plan.detail}</p>
                      </div>
                      <h3>{plan.name}</h3>
                      <div className="plan-price">
                        {plan.originalPrice ? <span className="plan-price__original">{plan.originalPrice}</span> : null}
                        <div className="plan-price__main">
                          <strong>{displayPrice}</strong>
                          <span className="plan-price__term">
                            {plan.priceTerm}
                            {renewalPrice ? <> · then {renewalPrice}</> : null}
                          </span>
                        </div>
                      </div>
                      <button
                        className="plan-card__toggle"
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={planDetailsId}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedPlan(plan.id);
                          togglePlanDetails(plan.id);
                        }}
                      >
                        <span>{isExpanded ? "Hide details" : "See what's included"}</span>
                        {isExpanded ? <ChevronUp size={18} strokeWidth={2.2} /> : <ChevronDown size={18} strokeWidth={2.2} />}
                      </button>
                      <div className="plan-card__details" id={planDetailsId}>
                        <p className="plan-description">{plan.description}</p>
                        <ul>
                          {plan.features.map((feature) => (
                            <li key={feature}><Icon name="check" /> {feature}</li>
                          ))}
                        </ul>
                      </div>
                    </article>
                  );
                })}
              </div>
              <LegalAgreementNote className="legal-agreement-note--pricing" />
              {showPlanComparisonToggle ? (
                <button
                  className="compare-plans-toggle"
                  type="button"
                  aria-expanded={showPlanComparison}
                  onClick={() => setShowPlanComparison((current) => !current)}
                >
                  {showPlanComparison ? "Hide plan comparison" : "Compare all plans"}
                  {showPlanComparison ? <ChevronUp size={18} strokeWidth={2.2} /> : <ChevronDown size={18} strokeWidth={2.2} />}
                </button>
              ) : null}
              <section className="payment-trust-proof" aria-label="Linkhiv customer trust">
                <WelcomeTrustBadge compact />
              </section>
              <div className="value-chips value-chips--marquee" aria-label="Linkhiv value highlights">
                <div className="value-chips__track">
                  {[...activeValueChips, ...activeValueChips].map((chip, index) => (
                    <span key={`${chip}-${index}`} aria-hidden={index >= activeValueChips.length}>
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
              <section className="checkout-next-steps" aria-label="What happens after payment">
                <div className="checkout-next-steps__copy">
                  <p className="eyebrow">After payment</p>
                  <h3>The next 30 seconds are simple</h3>
                </div>
                <ol className="checkout-timeline">
                  {activeCheckoutAfterPaymentSteps.map((item, index) => (
                    <li key={item.title}>
                      <span className="checkout-timeline__index">
                        {"icon" in item && item.icon === "bell" ? (
                          <Bell size={16} strokeWidth={2.3} aria-hidden="true" />
                        ) : (
                          index + 1
                        )}
                      </span>
                      <p>
                        <strong>{item.title}</strong>
                        {"subtitle" in item ? <span>{item.subtitle}</span> : null}
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
              <section className="payment-testimonials" aria-label="Customer reviews">
                <div className="payment-rating-proof" aria-label="Rated 4.9 out of 5 by 2,000 Mac users">
                  <div className="payment-rating-proof__stars" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star key={index} size={18} strokeWidth={2.1} />
                    ))}
                  </div>
                  <p>Rated 4.9/5 by 2,000+ Mac users</p>
                </div>
                <SocialProofScroller />
              </section>
              <details className="product-highlight product-highlight--collapsible">
                <summary className="product-highlight__summary">
                  <span>
                    <span className="eyebrow">How Linkhiv works</span>
                    <strong>Explore the app workflow</strong>
                  </span>
                  <ChevronDown size={20} strokeWidth={2.2} aria-hidden="true" />
                </summary>
                <div className="product-highlight__content">
                  <div className="product-highlight__copy">
                    <h3>Native Mac app that keeps what you copy at your fingertips</h3>
                  </div>
                  <div className="payment-explainer" aria-label="How Linkhiv works step by step">
                    {paymentExplainerSteps.map((step) => (
                      <article
                        key={step.title}
                        className={`payment-explainer-card tone-${step.tone}${step.bonus ? " payment-explainer-card--bonus" : ""}`}
                      >
                        {step.bonus ? <span className="payment-explainer-bonus__label">Bonus</span> : null}
                        {!step.bonus ? (
                          <span className="option-dot" aria-hidden="true">
                            <OptionIcon name={step.icon} />
                          </span>
                        ) : null}
                        <strong>{step.title}</strong>
                        <p>{step.description}</p>
                      </article>
                    ))}
                  </div>
                </div>
              </details>
              <section className="faq-block" aria-label="Frequently asked questions">
                <p className="eyebrow">FAQ</p>
                <h3>Questions before checkout?</h3>
                <div className="faq-list">
                  {activePaymentFaqs.map((faq) => (
                    <details key={faq.question} className="faq-item">
                      <summary>{faq.question}</summary>
                      <p>{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          ) : null}

          {error ? <p className="error-message">{error}</p> : null}
        </section>

        {showPayment ? (
        <aside className="side-proof" aria-label="Linkhiv proof">
          <div className="mini-proof">
            <img src={brand.iconPath} alt="" />
            <div>
              <strong>Linkhiv</strong>
              <span>Private by design</span>
            </div>
          </div>
          <div className="side-proof__cta">
            {!showDedicatedEmailStep ? (
              <div className="payment-email-inline">
                <label className="payment-email-inline__label" htmlFor="checkout-email-sidebar">
                  Used primarily for app activation and receipt
                </label>
                <input
                  id="checkout-email-sidebar"
                  className="payment-email-inline__input"
                  type="email"
                  value={email}
                  placeholder="you@example.com"
                  inputMode="email"
                  autoComplete="email"
                  aria-label="Email for activation details"
                  onBlur={(event) => captureEmailLead(event.target.value)}
                  onChange={(event) => setEmail(event.target.value)}
                />
                {showInlineEmailValidation ? (
                  <p className="payment-email-inline__hint payment-email-inline__hint--warning">
                    Enter a valid email, or leave it blank and add it in checkout.
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="checkout-card-rating" aria-label="Rated 4.9 out of 5 by 2,000 Mac users">
              <span className="checkout-card-rating__stars" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star key={index} size={15} strokeWidth={2.1} />
                ))}
              </span>
              <span>Rated 4.9/5 by 2,000+ Mac users</span>
            </div>
            <button
              className="button button-primary checkout-cta-button"
              type="button"
              disabled={isLoading}
              onClick={() => startCheckout(activeSelectedPlan)}
            >
              {selectedPlanCtaLabel}
            </button>
            <p className="side-proof__cta-note">{sideCtaNote}</p>
            <p className="checkout-guarantee" aria-label="Money-back guarantee">
              <Icon name="shield" />
              <span>
                <strong>30-day money-back guarantee</strong>
                <small>If Linkhiv is not a fit.</small>
              </span>
            </p>
          </div>
          <div className="proof-metrics">
            <span>
              <Icon name="shield" />
              <strong>Local-only history</strong>
            </span>
            <span>
              <Icon name="check" />
              <strong>{checkoutSecureLabel}</strong>
            </span>
            <span>
              <Icon name="bolt" />
              <strong>Instant access</strong>
            </span>
          </div>
        </aside>
        ) : null}
      </main>
      {showWelcome || showPayment ? (
        <footer className="funnel-footer">
          <a href={brand.termsUrl} target="_blank" rel="noreferrer">
            Terms
          </a>
          <a href={brand.privacyPolicyUrl} target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
        </footer>
      ) : null}
      {showPayment ? (
        <div className="floating-offer-timer" aria-live="polite">
          <span className="offer-timer__label">Limited-time offer</span>
          <strong>{offerMinutes}:{offerSeconds}</strong>
        </div>
      ) : null}
      {showPayment && showFloatingCheckoutCta ? (
        <div className="floating-checkout-cta">
          {!showDedicatedEmailStep ? (
            <div className="payment-email-inline">
              <label className="payment-email-inline__label" htmlFor="checkout-email-floating">
                Used primarily for app activation and receipt
              </label>
              <input
                id="checkout-email-floating"
                className="payment-email-inline__input"
                type="email"
                value={email}
                placeholder="you@example.com"
                inputMode="email"
                autoComplete="email"
                aria-label="Email for activation details"
                onBlur={(event) => captureEmailLead(event.target.value)}
                onChange={(event) => setEmail(event.target.value)}
              />
              {showInlineEmailValidation ? (
                <p className="payment-email-inline__hint payment-email-inline__hint--warning">
                  Enter a valid email, or leave it blank and add it in checkout.
                </p>
              ) : null}
            </div>
          ) : null}
          <button
            className="button button-primary checkout-cta-button"
            type="button"
            disabled={isLoading}
            onClick={() => startCheckout(activeSelectedPlan)}
          >
            {selectedPlanCtaLabel}
          </button>
          <p className="floating-checkout-cta__trust">{floatingCtaTrust}</p>
        </div>
      ) : null}
      {pendingFeedback ? (
        <div className="floating-feedback-cta">
          <button
            className="button button-primary checkout-cta-button"
            type="button"
            onClick={() => {
              setPendingFeedbackQuestionId(null);
              setStep((current) => current + 1);
            }}
          >
            Continue
          </button>
        </div>
      ) : null}
      {showResult ? (
        <div className="floating-result-cta">
          <button
            className="button button-primary checkout-cta-button"
            type="button"
            onClick={advanceFromResult}
          >
            Continue
          </button>
        </div>
      ) : null}
    </div>
  );
}
